<template>
  <div class="rbac-container">
    <div class="col-sm-12">

      <!-- Role Name + Status Toggle -->
      <div class="py-3 d-flex align-items-end gap-3">
        <div class="role-input">
          <label for="UserName" class="field-label">User Role:</label>
          <input
            id="UserName"
            type="text"
            v-model="UserName"
            class="form-control"
            required
          />
        </div>
        <div class="form-check form-switch role-toggle">
          <input
            class="form-check-input toggle-switch"
            type="checkbox"
            id="isActive"
            v-model="isActive"
          />
          <label
            class="form-check-label toggle-label"
            for="isActive"
            :class="isActive ? 'label-active' : 'label-inactive'"
          >
            {{ isActive ? 'Active' : 'Inactive' }}
          </label>
        </div>
      </div>

      <!-- Permissions Section -->
      <div class="py-3 w-100">
        <p class="field-label mb-3">Select User Access</p>

        <!-- Loading -->
        <div v-if="loadingModules" class="loading-box">
          <span class="spinner-border spinner-border-sm text-primary me-2"></span>
          <span class="loading-text">Loading permissions...</span>
        </div>

        <!-- Module Cards -->
        <div v-else class="modules-list">
          <div
            v-for="module in modules"
            :key="module.module_id"
            class="module-card"
            :class="{ 'module-card--active': isModulePartiallySelected(module) }"
          >
            <!-- Module Header -->
            <div class="module-header" @click="toggleModule(module.module_id)">
              <div class="module-header-left">
                <input
                  type="checkbox"
                  class="module-checkbox"
                  :checked="isModuleFullySelected(module)"
                  :indeterminate.prop="isModulePartiallySelected(module) && !isModuleFullySelected(module)"
                  @click.stop
                  @change="toggleSelectAllModule(module)"
                />
                <span class="module-name">{{ module.module_name }}</span>
              </div>
              <span class="toggle-btn">
                {{ expandedModules[module.module_id] ? '▲' : '▼' }}
              </span>
            </div>

            <!-- Module Body -->
            <div v-if="expandedModules[module.module_id]" class="module-body">

              <!-- Direct routes (no submodule) -->
              <div v-if="module.directRoutes.length" class="body-section">
                <div class="routes-wrap">
                  <label
                    v-for="route in module.directRoutes"
                    :key="route.module_route_id"
                    class="route-pill"
                    :class="{ 'route-pill--checked': selectedRoutes[route.module_route_id] }"
                  >
                    <input
                      type="checkbox"
                      v-model="selectedRoutes[route.module_route_id]"
                      class="route-check"
                    />
                    {{ route.route_name }}
                  </label>
                </div>
              </div>

              <!-- Submodules -->
              <div
                v-for="sub in module.submodules"
                :key="sub.submodule_id"
                class="body-section"
              >
                <div class="submodule-header">
                  <input
                    type="checkbox"
                    class="sub-checkbox"
                    :checked="isSubmoduleFullySelected(sub)"
                    :indeterminate.prop="isSubmodulePartiallySelected(sub) && !isSubmoduleFullySelected(sub)"
                    @change="toggleSelectAllSubmodule(sub)"
                  />
                  <span class="submodule-name">{{ sub.submodule_name }}</span>
                </div>
                <div class="routes-wrap">
                  <label
                    v-for="route in sub.routes"
                    :key="route.module_route_id"
                    class="route-pill"
                    :class="{ 'route-pill--checked': selectedRoutes[route.module_route_id] }"
                  >
                    <input
                      type="checkbox"
                      v-model="selectedRoutes[route.module_route_id]"
                      class="route-check"
                    />
                    {{ route.route_name }}
                  </label>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="text-end mt-4">
        <button class="btn btn-danger me-2" @click="Cancel" :disabled="loading">Cancel</button>
        <button class="btn btn-success" @click="SaveUserRole" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          {{ loading ? 'Updating...' : 'Update' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { fetchUserRoleDetailsByRoleId, updateUserRole } from '@/services/dataService';
import { useToast } from 'vue-toastification';

export default {
  data() {
    return {
      token: localStorage.getItem('token_set') || '',
      UserName: '',
      isActive: false,
      userId: this.$route.params.id || 1,
      modules: [],           // [{ module_id, module_name, directRoutes[], submodules[] }]
      selectedRoutes: {},    // { [module_route_id]: boolean }
      expandedModules: {},   // { [module_id]: boolean }
      errors: { name: '' },
      loading: false,
      loadingModules: false,
    };
  },

  created() {
    this.loadUserRoleDetails();
  },

  methods: {
    // ─── Data Loading ────────────────────────────────────────────────────────

    async loadUserRoleDetails() {
      this.loadingModules = true;
      try {
        const response = await fetchUserRoleDetailsByRoleId(this.userId, this.token);
        const userData = response.data.data;
        this.UserName = userData.role_name || '';
        this.isActive = userData.role_active === '1';

        const access = Array.isArray(userData.access)
          ? userData.access
          : Object.values(userData.access || {});

        this.modules = this.reshapeData(access);
      } catch (error) {
        console.error("Error loading user role details:", error);
      } finally {
        this.loadingModules = false;
      }
    },

    reshapeData(rawList) {
      const moduleMap = {};

      rawList.forEach((item) => {
        const {
          module_id, module_name,
          submodule_id, submodule_name,
          module_route_id, route_name,
          user_role_access_active,
        } = item;

        if (!moduleMap[module_id]) {
          moduleMap[module_id] = {
            module_id,
            module_name,
            directRoutes: [],
            submodules: {},
          };
        }

        const route = { module_route_id, route_name };

        if (!submodule_id) {
          moduleMap[module_id].directRoutes.push(route);
        } else {
          if (!moduleMap[module_id].submodules[submodule_id]) {
            moduleMap[module_id].submodules[submodule_id] = {
              submodule_id,
              submodule_name,
              routes: [],
            };
          }
          moduleMap[module_id].submodules[submodule_id].routes.push(route);
        }

        // Pre-check active routes
        if (user_role_access_active === '1') {
          this.selectedRoutes[module_route_id] = true;
        }
      });

      // Build modules array and auto-expand those with active routes
      return Object.values(moduleMap).map((mod) => {
        const submodules = Object.values(mod.submodules);
        const hasActive =
          mod.directRoutes.some((r) => this.selectedRoutes[r.module_route_id]) ||
          submodules.some((sub) => sub.routes.some((r) => this.selectedRoutes[r.module_route_id]));

        this.expandedModules[mod.module_id] = hasActive;

        return { ...mod, submodules };
      });
    },

    // ─── Expand / Collapse ───────────────────────────────────────────────────

    toggleModule(moduleId) {
      this.expandedModules[moduleId] = !this.expandedModules[moduleId];
    },

    // ─── Selection Helpers ───────────────────────────────────────────────────

    getAllModuleRouteIds(module) {
      const ids = module.directRoutes.map((r) => r.module_route_id);
      module.submodules.forEach((sub) => sub.routes.forEach((r) => ids.push(r.module_route_id)));
      return ids;
    },

    isModuleFullySelected(module) {
      const ids = this.getAllModuleRouteIds(module);
      return ids.length > 0 && ids.every((id) => this.selectedRoutes[id]);
    },

    isModulePartiallySelected(module) {
      return this.getAllModuleRouteIds(module).some((id) => this.selectedRoutes[id]);
    },

    isSubmoduleFullySelected(sub) {
      return sub.routes.length > 0 && sub.routes.every((r) => this.selectedRoutes[r.module_route_id]);
    },

    isSubmodulePartiallySelected(sub) {
      return sub.routes.some((r) => this.selectedRoutes[r.module_route_id]);
    },

    // ─── Select All ──────────────────────────────────────────────────────────

    toggleSelectAllModule(module) {
      const newValue = !this.isModuleFullySelected(module);
      this.getAllModuleRouteIds(module).forEach((id) => { this.selectedRoutes[id] = newValue; });
      if (newValue) this.expandedModules[module.module_id] = true;
    },

    toggleSelectAllSubmodule(sub) {
      const newValue = !this.isSubmoduleFullySelected(sub);
      sub.routes.forEach((r) => { this.selectedRoutes[r.module_route_id] = newValue; });
    },

    // ─── Save ────────────────────────────────────────────────────────────────

    async SaveUserRole() {
      this.loading = true;
      try {
        const payload = {
          is_active: this.isActive ? '1' : '0',
          user_role_name: this.UserName,
          user_role_id: this.userId,
          module_route_id: Object.entries(this.selectedRoutes)
            .filter(([, checked]) => checked)
            .map(([id]) => ({ module_route_id: parseInt(id) })),
        };

        await updateUserRole(this.token, payload);
        useToast().success("User role updated successfully!");
        this.$router.push('/user-roles');
      } catch (error) {
        console.error("Error updating user role:", error);
        const errorMessage = error.response?.data?.message || "Failed to update user role";
        useToast().error(errorMessage);
      } finally {
        this.loading = false;
      }
    },

    Cancel() {
      this.$router.push('/user-roles');
    },
  },
};
</script>

<style scoped>
/* ── Container ─────────────────────────────────────────────────────────── */
.rbac-container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 24px;
  margin: 10px;
  width: 100%;
  box-sizing: border-box;
}

/* ── Field Label ───────────────────────────────────────────────────────── */
.field-label {
  font-weight: 700;
  font-size: 14px;
  color: #2d3748;
}

/* ── Role Input Row ────────────────────────────────────────────────────── */
.role-input {
  flex: 0 0 75%;
}

.role-toggle {
  flex: 0 0 25%;
  display: flex;
  align-items: center;
}

.form-control {
  font-size: 14px;
  width: 100%;
}

.toggle-label {
  margin-top: 10px;
  margin-left: 10px;
  font-weight: 600;
}

.label-active  { color: #28a745; }
.label-inactive { color: #dc3545; }

/* ── Toggle Switch ─────────────────────────────────────────────────────── */
.toggle-switch {
  width: 80px;
  height: 18px;
  cursor: pointer;
  border-radius: 20px !important;
  transition: background-color 0.3s, border-color 0.3s;
}

.toggle-switch:checked {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
}

.toggle-switch:not(:checked) {
  background-color: #dc3545 !important;
  border-color: #dc3545 !important;
}

.toggle-switch::before {
  width: 16px !important;
  height: 16px !important;
  top: 3px !important;
  left: 3px !important;
  background-color: #fff !important;
  border-radius: 50%;
}

/* ── Loading ───────────────────────────────────────────────────────────── */
.loading-box {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  border: 1.5px dashed #cbd5e0;
  border-radius: 8px;
  background: #f7f9fc;
  gap: 10px;
}

.loading-text {
  font-size: 14px;
  color: #4a6fa5;
  font-weight: 500;
}

/* ── Module List ───────────────────────────────────────────────────────── */
.modules-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ── Module Card ───────────────────────────────────────────────────────── */
.module-card {
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.module-card--active {
  border-color: #7b8fe0;
  box-shadow: 0 2px 8px rgba(99, 117, 217, 0.12);
}

/* ── Module Header ─────────────────────────────────────────────────────── */
.module-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 16px;
  background-color: #eef0fa;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s;
}

.module-card--active .module-header {
  background-color: #e4e8f7;
}

.module-header:hover {
  background-color: #e0e4f5;
}

.module-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.module-name {
  font-weight: 700;
  font-size: 13px;
  color: #253069;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.module-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #253069;
  flex-shrink: 0;
}

.toggle-btn {
  font-size: 11px;
  color: #6375a8;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: rgba(99, 117, 168, 0.1);
  transition: background-color 0.15s;
}

.module-header:hover .toggle-btn {
  background-color: rgba(99, 117, 168, 0.2);
}

/* ── Module Body ───────────────────────────────────────────────────────── */
.module-body {
  background-color: #ffffff;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
}

/* ── Body Section ───────────────────────────────────────────────────────── */
.body-section {
  padding: 12px 16px;
  border-bottom: 1px solid #edf0f7;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.body-section:last-child {
  border-bottom: none;
}

/* ── Submodule Section ─────────────────────────────────────────────────── */
.submodule-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.submodule-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.submodule-name {
  font-size: 13px;
  font-weight: 600;
  color: #3d5a9a;
}

.sub-checkbox {
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: #3d5a9a;
  flex-shrink: 0;
}

/* ── Routes Wrap ───────────────────────────────────────────────────────── */
.routes-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding-left: 4px;
}

/* ── Route Pill ────────────────────────────────────────────────────────── */
.route-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1.5px solid #e2e8f0;
  background-color: #f8f9fc;
  font-size: 13px;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
  white-space: nowrap;
}

.route-pill:hover {
  border-color: #7b8fe0;
  background-color: #eef1fc;
  color: #253069;
}

.route-pill--checked {
  border-color: #4a6fa5;
  background-color: #eef2ff;
  color: #253069;
  font-weight: 500;
}

.route-check {
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: #253069;
  flex-shrink: 0;
}

/* ── Spinner ───────────────────────────────────────────────────────────── */
.spinner-border { vertical-align: middle; }

/* ── Layout Utilities ──────────────────────────────────────────────────── */
.d-flex              { display: flex; }
.align-items-end     { align-items: flex-end; }
.justify-content-end { justify-content: flex-end; }
.gap-3               { gap: 1rem; }
.py-3                { padding-top: 1rem; padding-bottom: 1rem; }
.w-100               { width: 100%; }
.mt-2                { margin-top: 0.5rem; }
</style>